import { ArrowUpRight, RefreshCw } from "lucide-react"
import { useEffect, useState } from "react"
import { cn } from "../../utils/cn"

type Source = "default" | "draft" | "custom"
interface Field<T = string> {
	value: T
	source: Source
}
interface Theme {
	name: Field
	heroImage: Field
	headline: Field
	logo: Field
	colour: Field
	font: Field<{ family: string; webLoadable: boolean }>
	uses: { logo: string[]; colour: string[]; font: string[] }
}
type Optional = keyof Theme["uses"]
type TextKey = "name" | "heroImage" | "headline" | "logo" | "colour"

const SOURCE: Record<Source, string> = { default: "Default", draft: "Drafted from your site", custom: "Edited by you" }
const ALWAYS: Array<{ key: TextKey; label: string; usedOn: string; hint?: string }> = [
	{ key: "name", label: "Brand name", usedOn: "Brand page header, in Shortlist's type" },
	{ key: "heroImage", label: "Hero image", usedOn: "Brand page, behind the headline", hint: "https:// image URL" },
	{ key: "headline", label: "Headline", usedOn: "Brand page, over the hero image" },
]
const OPTIONAL: Array<{ key: Optional; label: string; uses: Array<[string, string]> }> = [
	{
		key: "logo",
		label: "Logo",
		uses: [
			["shared_page", "Shared list page"],
			["widget_header", "Widget header"],
		],
	},
	{
		key: "colour",
		label: "Brand colour",
		uses: [
			["accents", "Hearts and the Ask AI button"],
			["share_images", "Share images"],
		],
	},
	{
		key: "font",
		label: "Brand font",
		uses: [
			["hero_headline", "Brand page headline"],
			["shared_title", "Shared list title"],
		],
	},
]
const CONTACT = "919810641489"

// Search admin → Appearance: what the brand page shows, and where the optional brand touches go.
export function BrandThemeSettings({
	getAuthHeader,
	storeUrl,
}: {
	getAuthHeader: () => Promise<Record<string, string>>
	storeUrl?: string
}) {
	const base = `${import.meta.env.VITE_BACKEND_URL || ""}/api/v1/search/config/brand-theme`
	const [theme, setTheme] = useState<Theme | null>(null)
	const [editing, setEditing] = useState<string | null>(null)
	const [draft, setDraft] = useState("")
	const [busy, setBusy] = useState("")
	const [message, setMessage] = useState<{ error: boolean; text: string } | null>(null)

	async function request(path = "", method = "GET", body?: unknown) {
		const response = await fetch(`${base}${path}`, {
			method,
			headers: { "Content-Type": "application/json", ...(await getAuthHeader()) },
			...(body ? { body: JSON.stringify(body) } : {}),
		})
		const result = await response.json()
		if (!response.ok) throw new Error(result.error || "Request failed")
		return result as Theme
	}

	useEffect(() => {
		request()
			.then(setTheme)
			.catch((error) => setMessage({ error: true, text: error.message }))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	async function save(body: unknown, label: string, done = "Saved") {
		setBusy(label)
		setMessage(null)
		try {
			setTheme(await request("", "PUT", body))
			setEditing(null)
			setMessage({ error: false, text: done })
		} catch (error) {
			setMessage({ error: true, text: error instanceof Error ? error.message : "Could not save" })
		} finally {
			setBusy("")
		}
	}

	async function redraft() {
		setBusy("draft")
		setMessage(null)
		try {
			setTheme(await request("/draft", "POST"))
			setMessage({ error: false, text: "Redrafted everything you haven't edited" })
		} catch (error) {
			setMessage({ error: true, text: error instanceof Error ? error.message : "Could not redraft" })
		} finally {
			setBusy("")
		}
	}

	const shown = (key: string) => {
		if (!theme) return ""
		if (key === "font") return theme.font.value.family
		return (theme as any)[key].value as string
	}
	const startEdit = (key: string) => {
		setEditing(key)
		setDraft(shown(key))
	}
	const submit = (key: string) => {
		const value = draft.trim()
		if (key === "font") return save({ font: { family: value, webLoadable: Boolean(value) } }, key) // The hint asks for a Google Fonts family.
		return save({ [key]: key === "colour" ? value.toLowerCase() : value }, key)
	}
	const toggleUse = (key: Optional, use: string, on: boolean) => {
		const current = theme!.uses[key]
		return save({ uses: { [key]: on ? [...current, use] : current.filter((u) => u !== use) } }, `${key}:${use}`)
	}
	const requestMore = `https://wa.me/${CONTACT}?text=${encodeURIComponent(
		`Hi! I'd like more customisation for our Shortlist brand page${storeUrl ? ` (${storeUrl})` : ""}.`,
	)}`

	const editor = (key: string, hint?: string) =>
		editing === key ? (
			<form
				className="flex gap-2 mt-1"
				onSubmit={(e) => {
					e.preventDefault()
					void submit(key)
				}}>
				<input
					aria-label={`New ${key}`}
					autoFocus
					className="flex-1 px-2 py-1 border border-border rounded text-sm bg-background"
					onChange={(e) => setDraft(e.target.value)}
					placeholder={hint}
					value={draft}
				/>
				<button className="px-2 py-1 border border-border rounded text-sm" disabled={!!busy} type="submit">
					Save
				</button>
				<button className="px-2 py-1 text-sm text-muted-foreground" onClick={() => setEditing(null)} type="button">
					Cancel
				</button>
			</form>
		) : (
			<button className="text-xs underline text-muted-foreground" onClick={() => startEdit(key)} type="button">
				Edit
			</button>
		)

	const sourceOf = (key: string) => (theme ? SOURCE[((theme as any)[key] as Field<unknown>).source] : "")
	const row = "grid gap-3 sm:grid-cols-[140px_1fr_220px] py-3 border-t border-border text-sm"

	return (
		<section className="bg-card border border-border rounded-lg p-6 mb-6">
			<div className="flex items-center justify-between mb-1">
				<h2 className="text-lg font-semibold">Brand page &amp; widget</h2>
				<button
					className="px-3 py-2 border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
					disabled={!!busy || !theme}
					onClick={redraft}>
					<RefreshCw className={cn("w-4 h-4", busy === "draft" && "animate-spin")} />
					{busy === "draft" ? "Redrafting..." : "Redraft from site"}
				</button>
			</div>
			<p className="text-sm text-muted-foreground mb-3">
				Drafted from your catalogue and site. Anything you edit is kept and never redrafted. The page uses Shortlist's
				own styling; logo, colour and font appear only where you turn them on.
			</p>
			{!theme ? (
				!message && <p className="text-sm text-muted-foreground">Loading...</p>
			) : (
				<div>
					<div className="hidden sm:grid gap-3 sm:grid-cols-[140px_1fr_220px] text-xs text-muted-foreground pb-1">
						<span>Item</span>
						<span>Value</span>
						<span>Used on</span>
					</div>
					{ALWAYS.map(({ key, label, usedOn, hint }) => (
						<div className={row} key={key}>
							<b className="font-medium">{label}</b>
							<div className="min-w-0">
								{key === "heroImage" && theme.heroImage.value && (
									<img alt="" className="h-16 w-12 object-cover rounded mb-1" src={theme.heroImage.value} />
								)}
								<div className="truncate">{theme[key].value || <i className="text-muted-foreground">Not set</i>}</div>
								<div className="text-xs text-muted-foreground">{sourceOf(key)}</div>
								{editor(key, hint)}
							</div>
							<span className="text-muted-foreground">{usedOn} · always</span>
						</div>
					))}
					{OPTIONAL.map(({ key, label, uses }) => {
						const value = shown(key)
						const unloadable = key === "font" && value && !theme.font.value.webLoadable
						return (
							<div className={row} key={key}>
								<b className="font-medium">{label}</b>
								<div className="min-w-0">
									<div className="flex items-center gap-2">
										{key === "colour" && value && (
											<span className="inline-block w-4 h-4 rounded border border-border" style={{ background: value }} />
										)}
										{key === "logo" && value && <img alt="" className="h-6 max-w-[120px] object-contain" src={value} />}
										<span className="truncate">{value || <i className="text-muted-foreground">None found</i>}</span>
									</div>
									<div className="text-xs text-muted-foreground">
										{sourceOf(key)}
										{unloadable && " · can't be loaded on the web, Shortlist's font is used instead"}
									</div>
									{editor(key, key === "colour" ? "#aa3344" : key === "logo" ? "https:// image URL" : "Google Fonts family")}
								</div>
								<div className="flex flex-col gap-1">
									{uses.map(([use, text]) => (
										<label className={cn("flex items-center gap-2", (!value || unloadable) && "opacity-50")} key={use}>
											<input
												checked={theme.uses[key].includes(use)}
												disabled={!value || !!unloadable || !!busy}
												onChange={(e) => toggleUse(key, use, e.target.checked)}
												type="checkbox"
											/>
											{text}
										</label>
									))}
								</div>
							</div>
						)
					})}
					<div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-border">
						<span className="text-sm text-muted-foreground">Want your own fonts, layout or more images?</span>
						<a
							className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm flex items-center gap-1"
							href={requestMore}
							rel="noreferrer"
							target="_blank">
							Request more customisation <ArrowUpRight className="w-3 h-3" />
						</a>
					</div>
				</div>
			)}
			{message && <p className={cn("text-sm mt-3", message.error ? "text-red-600" : "text-green-700")}>{message.text}</p>}
		</section>
	)
}
